import { useEffect, useState } from 'react';
import { Crown, ArrowRight, Sparkles, X } from 'lucide-react';
import { HexAccent } from './HexMotif';

/**
 * Full-screen welcome experience shown on a carrier's first login.
 * Tracks dismissal in localStorage so it only appears once per user.
 */
const WelcomeConcierge = ({ userId, firstName = 'Carrier', onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const key = `qb_welcome_seen_${userId}`;
    if (!localStorage.getItem(key)) {
      setIsVisible(true);
    }
  }, [userId]);

  const handleDismiss = () => {
    if (userId) localStorage.setItem(`qb_welcome_seen_${userId}`, new Date().toISOString());
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="welcome-concierge-modal"
    >
      <div className="relative max-w-2xl w-full my-8">
        {/* Decorative hex accents */}
        <HexAccent size={80} color="#D4AF37" className="absolute -top-4 -left-4 opacity-10" />
        <HexAccent size={60} color="#D4AF37" className="absolute -bottom-6 -right-4 opacity-10" />

        <div className="relative bg-gradient-to-br from-[#121212] to-[#0A0A0A] border-2 border-[#D4AF37]/40 p-6 sm:p-8 md:p-12">
          {/* Close */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 hover:bg-white/10 transition-colors z-10"
            data-testid="welcome-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white/50" />
          </button>

          {/* Crown + Sparkle */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em]">
                A New Era Begins
              </span>
            </div>

            <Crown className="w-12 h-12 sm:w-14 sm:h-14 text-[#D4AF37] mx-auto mb-4" />

            <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl md:text-5xl font-black text-white uppercase tracking-wide leading-none mb-3 break-words">
              Welcome to<br />
              <span className="text-[#D4AF37]">The Hive</span>{firstName && firstName !== 'Carrier' ? `, ${firstName}` : ''}
            </h1>

            <p className="text-white/50 text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.2em]">
              Premium Freight Operations
            </p>
          </div>

          {/* Framework */}
          <div className="mb-8">
            <p className="text-white/80 leading-relaxed text-center mb-6">
              Queen B Logistics isn't another dispatch company. It's a full operating system for carriers
              who are done being treated like dispatchable inventory — built on three pillars:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FrameworkPillar
                title="Build"
                color="#F59E0B"
                description="We launch your authority with intention"
              />
              <FrameworkPillar
                title="Protect"
                color="#3B82F6"
                description="We safeguard your compliance with precision"
              />
              <FrameworkPillar
                title="Grow"
                color="#10B981"
                description="We scale your revenue with strategy"
              />
            </div>
          </div>

          {/* Differentiator */}
          <div className="border-l-2 border-[#D4AF37] bg-white/5 p-5 mb-8">
            <p className="text-white/70 italic leading-relaxed">
              "Most dispatch companies book loads. We <strong className="text-white">position, protect,
              and scale</strong> your entire operation. Every carrier in the Hive was intentionally selected —
              because a crowned network only scales with the right operators."
            </p>
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider mt-3 font-bold">
              — The Queen B Standard
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={handleDismiss}
            className="w-full py-4 bg-[#D4AF37] text-black font-black uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-3"
            data-testid="welcome-cta-btn"
          >
            Enter Your Command Center
            <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-white/30 text-center text-xs mt-4 italic">
            Crowned in confidence. Built for carriers who are ready.
          </p>
        </div>
      </div>
    </div>
  );
};

const FrameworkPillar = ({ title, color, description }) => (
  <div
    className="bg-black/40 border p-4 text-center"
    style={{ borderColor: `${color}40` }}
  >
    <h3
      className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-wider mb-1"
      style={{ color }}
    >
      {title}
    </h3>
    <p className="text-white/60 text-xs leading-snug">{description}</p>
  </div>
);

export default WelcomeConcierge;
